import { useEffect, useMemo, useRef, useState } from "react";
import { getJson, entityPath, nf, ConfidenceBadge, VERB_LABEL, VERB_HEADING, OBS_TYPES, card } from "./shared";
import { vizTokens } from "./Charts";

// Radial relationship graph: the entity in the middle, neighbours in one labelled
// sector per kind (groups, software, techniques, …). Type is carried by the sector,
// its label and the node icon — not by colour alone (the app's type colours are
// accents here; they don't separate reliably as a categorical palette).
// Edges encode confidence: thicker = higher, dashed = inferred.
// Clicking a node re-centres the graph on it, so the graph can be walked hop by hop.

const BUCKETS = [
  { k: "groups", l: "Groups" }, { k: "campaigns", l: "Campaigns" }, { k: "software", l: "Software" },
  { k: "techniques", l: "Techniques" }, { k: "mitigations", l: "Mitigations" }, { k: "vulnerabilities", l: "Vulnerabilities" }, { k: "indicators", l: "Indicators" }, { k: "other", l: "Related" },
];
const PER_BUCKET = 14;

function bucketOf(r) {
  if (r.aggregate || OBS_TYPES.has(r.other?.type)) return "indicators";
  const t = r.other?.type;
  if (t === "intrusion-set") return "groups";
  if (t === "campaign") return "campaigns";
  if (t === "malware" || t === "tool") return "software";
  if (t === "attack-pattern") return "techniques";
  if (t === "course-of-action") return "mitigations";
  if (t === "vulnerability") return "vulnerabilities";
  return "other";
}

const edgeWidth = (r) => (r.aggregate ? 1.5 : r.confidence?.label === "High" ? 2.25 : r.confidence?.label === "Medium" ? 1.5 : 1);
const short = (s, n = 20) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

function useWidth(ref) {
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(300, Math.round(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

export function RelationshipGraph({ subject, relationships, ui, onShowBucket }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const t = vizTokens(B);
  const boxRef = useRef(null);
  const width = useWidth(boxRef);
  const height = Math.round(Math.min(680, Math.max(420, width * 0.72)));

  // Walk state: the trail of centres visited, and the data for the current one.
  const [trail, setTrail] = useState([{ summary: subject, relationships }]);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showInferred, setShowInferred] = useState(true);

  useEffect(() => { setTrail([{ summary: subject, relationships }]); setSelected(null); }, [subject, relationships]);

  const current = trail[trail.length - 1];

  const recenter = async (r) => {
    if (!r.other?.id) return;
    setLoading(true);
    try {
      const d = await getJson(`/api/kb/entity/${encodeURIComponent(r.other.id)}`);
      setTrail(tr => [...tr, { summary: d.summary, relationships: d.relationships }]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const layout = useMemo(() => {
    const cx = width / 2, cy = height / 2;
    // Labels run outward along their spoke, so the margin must hold a full label.
    const margin = width < 520 ? 80 : 150;
    const ring = Math.max(60, Math.min(width, height) / 2 - margin);
    const maxChars = Math.max(6, Math.floor((margin - 22) / 6.2));
    const rels = current.relationships.filter(r => showInferred || !r.inferred);
    const groups = new Map();
    for (const r of rels) {
      const b = bucketOf(r);
      if (!groups.has(b)) groups.set(b, []);
      groups.get(b).push(r);
    }
    const rank = (r) => (r.aggregate ? 200 : (r.inferred ? 0 : 100) + (r.confidence?.score ?? 50));
    const present = BUCKETS.filter(b => groups.has(b.k)).map(b => ({ ...b, all: groups.get(b.k).sort((a, c) => rank(c) - rank(a)) }));

    // As many nodes as fit with ~16px of arc each (one text line), shared out across sectors:
    // every sector gets a few, the rest goes to the biggest.
    const gap = present.length > 1 ? 0.1 : 0;
    const capacity = Math.max(10, Math.floor(((2 * Math.PI - gap * present.length) * ring) / 16));
    const caps = present.map(s => Math.min(s.all.length, 3));
    let left = capacity - caps.reduce((a, b) => a + b, 0) - present.length; // reserve a slot per sector for "+N more"
    while (left > 0) {
      let best = -1, bestNeed = 0;
      present.forEach((s, i) => { const need = (s.all.length - caps[i]) / s.all.length; if (s.all.length > caps[i] && need > bestNeed) { bestNeed = need; best = i; } });
      if (best < 0) break;
      caps[best]++; left--;
    }
    const sectors = present.map((s, i) => {
      const shown = s.all.slice(0, Math.min(caps[i], PER_BUCKET * 4));
      return { ...s, shown, more: s.all.length - shown.length };
    });

    // Uniform spacing: each sector's span ∝ the nodes it draws.
    const counts = sectors.map(s => s.shown.length + (s.more ? 1 : 0));
    const total = counts.reduce((a, b) => a + b, 0) || 1;
    let angle = -Math.PI / 2;
    const nodes = [];
    const labels = [];
    sectors.forEach((s, si) => {
      const span = (2 * Math.PI - gap * sectors.length) * (counts[si] / total);
      const items = [...s.shown, ...(s.more ? [{ more: s.more, bucket: s.k }] : [])];
      items.forEach((r, i) => {
        const a = angle + span * ((i + 0.5) / items.length);
        nodes.push({ r, a, x: cx + ring * Math.cos(a), y: cy + ring * Math.sin(a), bucket: s.k });
      });
      // Sector title inside the ring, only where the sector is wide enough to hold it.
      const mid = angle + span / 2;
      const text = `${s.l} ${nf(s.all.length)}`;
      if (ring >= 140 && span * ring * 0.55 > text.length * 7.5) labels.push({ text, x: cx + ring * 0.55 * Math.cos(mid), y: cy + ring * 0.55 * Math.sin(mid) });
      angle += span + gap;
    });
    return { cx, cy, nodes, labels, sectors, maxChars };
  }, [current, width, height, showInferred]);

  const centerConf = getConf(current.summary.type);
  const active = hover ?? selected;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {trail.map((step, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {i > 0 && <span style={{ color: B.textDim }}>›</span>}
            <button
              onClick={() => { setTrail(tr => tr.slice(0, i + 1)); setSelected(null); }}
              disabled={i === trail.length - 1}
              style={{ background: "none", border: "none", padding: 0, cursor: i === trail.length - 1 ? "default" : "pointer", color: i === trail.length - 1 ? B.text : B.accent, fontWeight: i === trail.length - 1 ? 700 : 500, fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}
            >{short(step.summary.name, 28)}</button>
          </span>
        ))}
        <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: fs(12), color: B.textMuted, cursor: "pointer" }}>
          <input type="checkbox" checked={showInferred} onChange={e => setShowInferred(e.target.checked)} /> Inferred links
        </label>
      </div>

      <div ref={boxRef} style={{ ...card(ui), position: "relative", overflow: "hidden", opacity: loading ? 0.55 : 1, transition: "opacity 150ms" }}>
        <svg width={width} height={height} style={{ display: "block", maxWidth: "100%" }} role="img" aria-label={`Relationship graph for ${current.summary.name}`}>
          {/* Edges */}
          {layout.nodes.map((n, i) => (
            <line key={`e${i}`} x1={layout.cx} y1={layout.cy} x2={n.x} y2={n.y}
              stroke={active === i ? t.series1 : t.axis}
              strokeWidth={n.r.more ? 1 : edgeWidth(n.r) + (active === i ? 0.75 : 0)}
              strokeDasharray={n.r.inferred || n.r.more ? "4 4" : undefined}
              opacity={active === null || active === i ? 1 : 0.45} />
          ))}
          {/* Sector labels */}
          {layout.labels.map(l => (
            <text key={l.text} x={l.x} y={l.y} textAnchor="middle" fontSize={fs(10)} fontWeight={700} fill={B.textDim} style={{ textTransform: "uppercase", letterSpacing: 1.2 }}>{l.text}</text>
          ))}
          {/* Neighbour nodes */}
          {layout.nodes.map((n, i) => {
            const right = Math.cos(n.a) >= 0;
            // Label follows its spoke; on the left half it's flipped so it never reads upside down.
            const deg = (n.a * 180) / Math.PI + (right ? 0 : 180);
            const conf = n.r.more ? null : getConf(n.r.aggregate ? "indicator" : n.r.other?.type);
            const label = n.r.more ? `+${nf(n.r.more)} more` : short(n.r.other?.name || "", layout.maxChars);
            const act = () => (n.r.more ? onShowBucket?.(n.bucket) : setSelected(i));
            return (
              <g key={`n${i}`} transform={`translate(${n.x},${n.y})`} tabIndex={0} role="button"
                aria-label={n.r.more ? `${n.r.more} more ${n.bucket}` : `${n.r.other?.name}: ${VERB_LABEL[n.r.verb] || n.r.verb}${n.r.confidence ? `, ${n.r.confidence.label} confidence` : ""}`}
                onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                onClick={act} onDoubleClick={() => { if (!n.r.more && !n.r.aggregate && n.r.other?.id) window.location.hash = entityPath(n.r.other).replace(/^#/, ""); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); } }}
                style={{ cursor: "pointer", outline: "none" }}>
                <circle r={16} fill="transparent" />
                <circle r={n.r.more ? 7 : 10} fill={B.bgCard} stroke={active === i || selected === i ? t.series1 : B.borderLight} strokeWidth={active === i || selected === i ? 2 : 1} />
                {conf && <g transform="translate(-6,-6)"><TypeIcon type={n.r.aggregate ? "indicator" : n.r.other?.type} color={conf.color} size={12} /></g>}
                <text x={right ? 15 : -15} y={4} transform={`rotate(${deg})`} textAnchor={right ? "start" : "end"} fontSize={fs(11)} fill={active === i ? B.text : n.r.more ? B.accent : B.textMuted} fontWeight={active === i ? 700 : 500}>{label}</text>
              </g>
            );
          })}
          {/* Centre */}
          <g transform={`translate(${layout.cx},${layout.cy})`}>
            <circle r={26} fill={B.bgCard} stroke={centerConf.color} strokeWidth={2} />
            <g transform="translate(-10,-10)"><TypeIcon type={current.summary.type} color={centerConf.color} size={20} /></g>
            <text y={44} textAnchor="middle" fontSize={fs(13)} fontWeight={800} fill={B.text}>{short(current.summary.name, 30)}</text>
          </g>
        </svg>
        {layout.nodes.length === 0 && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: B.textDim, fontSize: fs(13) }}>No relationships to draw.</div>}
      </div>

      {/* Sector key: every kind drawn, with its full count; opens the list view */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
        {layout.sectors.map(s => (
          <button key={s.k} onClick={() => onShowBucket?.(s.k)} style={{ background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 20, padding: "4px 11px", cursor: onShowBucket ? "pointer" : "default", fontSize: fs(11), color: B.textMuted, fontFamily: "'Satoshi', sans-serif" }}>
            {s.l} <b style={{ color: B.text }}>{nf(s.all.length)}</b>{s.more ? <span style={{ color: B.textDim }}> · {nf(s.shown.length)} drawn</span> : null}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, fontSize: fs(11), color: B.textDim, alignItems: "center" }}>
        <span>Line weight = confidence:</span>
        {[["High", 2.25, false], ["Medium", 1.5, false], ["Low", 1, false], ["Inferred", 1, true]].map(([l, w, dash]) => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="26" height="6"><line x1="0" y1="3" x2="26" y2="3" stroke={t.axis} strokeWidth={w} strokeDasharray={dash ? "4 4" : undefined} /></svg>{l}
          </span>
        ))}
        <span>· Click a node to inspect it, double-click to open its profile.</span>
      </div>

      {selected !== null && layout.nodes[selected] && (() => {
        const r = layout.nodes[selected].r;
        const verb = VERB_LABEL[r.verb] || r.verb;
        const sentence = r.direction === "out" ? `${current.summary.name} ${verb} ${r.other?.name}` : `${r.other?.name} ${verb} ${current.summary.name}`;
        return (
          <div style={{ ...card(ui), marginTop: 12, padding: "14px 16px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <div style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.2 }}>{r.inferred ? "Inferred · " : ""}{VERB_HEADING[r.direction]?.[r.verb] || verb}</div>
              <div style={{ fontSize: fs(14), color: B.text, fontWeight: 700, marginTop: 2, wordBreak: "break-word" }}>{sentence}</div>
              {r.note && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 3 }}>{r.note}</div>}
            </div>
            <ConfidenceBadge confidence={r.confidence} ui={ui} />
            {!r.aggregate && r.other?.id && (
              <>
                <a href={entityPath(r.other)} style={{ background: B.primary + "22", border: `1px solid ${B.primary}66`, color: B.text, borderRadius: 8, padding: "7px 14px", fontSize: fs(12), fontWeight: 800, textDecoration: "none" }}>Open {r.other?.name?.length > 24 ? "profile" : r.other?.name} →</a>
                <button onClick={() => recenter(r)} style={{ background: "none", border: `1px solid ${B.border}`, color: B.textMuted, borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>Center graph here</button>
              </>
            )}
            {r.aggregate && onShowBucket && <button onClick={() => onShowBucket("indicators", r.aggregate.familyId)} style={{ background: "none", border: `1px solid ${B.border}`, color: B.textMuted, borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>View indicators</button>}
          </div>
        );
      })()}
    </div>
  );
}
